import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { CheckCircle2, ArrowRight, Sparkles, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { employerAPI } from "../../services/api";

/**
 * Visual service card for the Employer Intake Package.
 *
 * Primary CTA is the direct purchase button (Phase 1B M2). The secondary
 * "Ask us" link keeps the Phase 1A inquiry form available for prospects
 * who aren't ready to buy.
 *
 * The purchase route returns 503 while ``amount_cad`` is None in the
 * server-side pricing catalog — this component surfaces that as a
 * friendly "not yet available" toast rather than a crash.
 */
export const IntakePackageCard = () => {
  const navigate = useNavigate();
  const [buying, setBuying] = useState(false);
  const inclusions = [
    "We review the role you're hiring for and prepare tailored applicant materials",
    "Ready-to-use resources delivered by email",
    "A dedicated service reference for every purchase",
    "Follow-up support from the Inter-View team",
  ];

  const handleBuyNow = async () => {
    setBuying(true);
    try {
      const res = await employerAPI.createPurchaseCheckout({
        sku_version: "employer_intake_package_v1",
        origin_url: window.location.origin,
        // Purchaser identity is taken from the VERIFIED signed-in
        // employer account server-side; Stripe Checkout collects the
        // remaining billing fields on the hosted page.
        employer_name: "Pending — collected on Stripe Checkout",
        purchaser_name: "Pending — collected on Stripe Checkout",
        purchaser_email: "pending-checkout@inter-view.ca",
      });
      // Redirect to Stripe hosted checkout. This leaves our SPA.
      window.location.assign(res.checkout_url);
    } catch (err) {
      const status = err?.response?.status;
      const rawDetail = err?.response?.data?.detail;
      const code = typeof rawDetail === "object" ? rawDetail?.code : null;
      const detail = typeof rawDetail === "object" ? rawDetail?.message : rawDetail;
      if (status === 401) {
        toast.info("Please sign in with your employer account to purchase.", { duration: 6000 });
        navigate("/signup");
      } else if (status === 403 && code === "email_verification_required") {
        toast.info(
          "Please verify your email address before purchasing — check your inbox for the verification link, or resend it from your Employer Hub.",
          { duration: 8000 }
        );
      } else if (status === 403) {
        toast.error(detail || "An employer account is required to purchase.");
      } else if (status === 503) {
        toast.info(
          "The Employer Intake Package is nearly ready — we're finalising launch pricing. Please use the ‘Ask us’ link below and we'll get back to you as soon as purchase is open.",
          { duration: 8000 }
        );
      } else if (status === 502) {
        toast.error("Payment provider is temporarily unavailable. Please try again in a moment.");
      } else {
        toast.error(detail || "Unable to start checkout — please try again.");
      }
      setBuying(false);
    }
  };

  return (
    <section
      id="intake-package"
      className="py-16 lg:py-20 bg-white"
      data-testid="employer-intake-package-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            The Employer Intake Package
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            A professional service, not a subscription. We prepare your onboarding
            materials so you can focus on hiring.
          </p>
        </div>

        <Card className="shadow-lg border-blue-100 overflow-hidden" data-testid="intake-package-card">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 pb-6">
            <div className="flex items-center gap-2 text-blue-700 text-sm font-semibold mb-2">
              <Sparkles className="h-4 w-4" />
              PROFESSIONAL SERVICE
            </div>
            <CardTitle className="text-2xl text-gray-900">
              Inter-View Employer Intake Package
            </CardTitle>
            <CardDescription className="text-base text-gray-700">
              A tailored kit prepared by the Inter-View team for your hiring context.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 lg:p-8">
            <ul className="space-y-3 mb-8" data-testid="intake-package-inclusions">
              {inclusions.map((line, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-gray-100 pt-6 space-y-4">
              <div>
                <Button
                  size="lg"
                  onClick={handleBuyNow}
                  disabled={buying}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white text-base px-8 shadow-md"
                  data-testid="intake-package-buy-now"
                >
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  {buying ? "Opening secure checkout…" : "Buy the Employer Intake Package"}
                </Button>
                <p className="text-sm text-gray-500 mt-3">
                  Payment is processed securely by Stripe. A receipt is emailed
                  automatically on successful payment.
                </p>
              </div>
              <div className="pt-4 border-t border-gray-50">
                <Link
                  to="/employers/intake"
                  className="text-sm text-blue-700 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                  data-testid="intake-package-inquiry-link"
                >
                  Have questions first? Ask us
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
